/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {FC, useEffect, useState} from 'react'
import axios from 'axios'
import {connect} from 'react-redux'
import {Link} from 'react-router-dom'
import {useHistory} from 'react-router'
import {useTranslation} from 'react-i18next'
import ClickAwayListener from 'react-click-away-listener'

import {useAuth} from '@be-infinity-ltd/auth-react'
import './header.css'
import {AppState} from '../../../../setup/redux/Store'
import {decryptPermissionStatus} from '../../../../config/utils'
import {SSO_ENABLED} from '../../../../setup/keycloak/authConfig'
import {clearSsoSession} from '../../../../app/modules/auth/ssoLogout'
import {BACKEND_UPLOAD_URL, BACKEND_API_URL, BACKEND_API_V2, removeAllSession} from '../../../../config/config'

interface TopbarProps {
  handleClickAway: () => void
  showToggleMenu: boolean
  inspectorMode: any
  inspectorId: any
  curUserPhotoUrl: any
  curUserFirstName: any
  curUserSecondName: any
  curUserEmail: any
}

const HeaderUserMenu: FC<TopbarProps> = ({
  handleClickAway,
  showToggleMenu,
  inspectorMode,
  inspectorId,
  curUserPhotoUrl,
  curUserFirstName,
  curUserSecondName,
  curUserEmail,
}) => {
  let tokenValue = localStorage.getItem('token')

  const [curFirstName, setCurFirstName] = useState(curUserFirstName || '')
  const [curSecondName, setCurSecondName] = useState(curUserSecondName || '')
  const [curEmail, setCurEmail] = useState(curUserEmail || '')
  const [curPhotoURL, setCurPhotoURL] = useState(curUserPhotoUrl || '')

  let permissionStatus = localStorage.getItem('data') as string
  let permissionVal = 0
  if (permissionStatus) {
    permissionVal = decryptPermissionStatus(permissionStatus)
  }

  const {t} = useTranslation()
  const history = useHistory()
  // Always called (hooks must be unconditional). Only used under SSO; inert when the flag is off
  // (AuthProvider is not mounted, so this is falsy — guarded below).
  const oidc = useAuth()

  useEffect(() => {
    // Update local state when Redux values change
    setCurPhotoURL(curUserPhotoUrl || '')
    setCurFirstName(curUserFirstName || '')
    setCurSecondName(curUserSecondName || '')
    setCurEmail(curUserEmail || '')
  }, [curUserPhotoUrl, curUserFirstName, curUserSecondName, curUserEmail])

  useEffect(() => {
    // Only fetch from API on initial mount or when inspector mode changes
    // Redux updates will be handled by the above useEffect
    getInitInformation()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inspectorMode])

  const getInitInformation = async () => {
    if (permissionVal === 1 && inspectorMode === 0) {
      const userData = await axios.get(`${BACKEND_API_URL}/getStaff`, {
        headers: {'x-access-token': `${tokenValue}`},
      })

      const {auth} = userData?.data
      if (auth === false) {
        removeAllSession()
        history.push('/staff/login')
      } else if (userData?.data !== undefined && userData?.data !== null) {
        setCurFirstName(userData?.data?.first_name)
        setCurSecondName(userData?.data?.last_name)
        setCurEmail(userData?.data?.email)
        setCurPhotoURL(userData?.data?.photo_url)
      }
    } else {
      const userData = await axios.get(
        `${BACKEND_API_V2}/getUser?inspectorId=${inspectorId}&inspectorMode=${inspectorMode}`,
        {
          headers: {'x-access-token': `${tokenValue}`},
        }
      )

      const {auth} = userData?.data
      if (auth === false) {
        removeAllSession()
        history.push('/auth/login')
      } else if (userData?.data !== undefined && userData?.data !== null) {
        setCurFirstName(userData?.data?.first_name)
        setCurSecondName(userData?.data?.second_name)
        setCurEmail(userData?.data?.email)
        setCurPhotoURL(userData?.data?.photo_url)
      }
    }
  }

  const logout = () => {
    localStorage.removeItem('token')
    localStorage.removeItem('security_flag')
    localStorage.removeItem('restriction_flag')
    localStorage.removeItem('confirm_flag')
    localStorage.removeItem('data')
    localStorage.removeItem('hashUserFlag')
    // Under SSO the legacy handler above (clearing a few localStorage keys) is NOT a logout: the
    // Keycloak session stays alive and AuthInit re-bridges the token, so the user stays signed in.
    // End the KC session via the OIDC end-session endpoint (full-page nav). Mark that we're logging
    // out so that when the app returns unauthenticated, AuthInit forces a fresh KC login prompt
    // (prompt=login) instead of silently re-issuing a token from a lingering session → back to dashboard.
    if (SSO_ENABLED && oidc) {
      // Synchronously purge the persisted redux-persist auth blob + token + oidc keys BEFORE navigating
      // to KC, so the app doesn't rehydrate as logged-in on return; then end the KC session. (Removing
      // the localStorage keys above is not enough — the redux-persist blob rehydrates token+user.)
      clearSsoSession(oidc)
    }
  }

  const handleClick = () => {
    if (showToggleMenu) handleClickAway()
  }

  return (
    <>
      <ClickAwayListener onClickAway={handleClick}>
        <div
          className={
            showToggleMenu === false
              ? 'menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-600 menu-state-bg menu-state-primary fw-bold py-4 fs-6 w-275px'
              : 'menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-600 menu-state-bg menu-state-primary fw-bold py-4 fs-6 w-275px show show-user-toggleMenu'
          }
          data-kt-menu='true'
        >
          <div className='menu-item px-3'>
            <div className='menu-content d-flex align-items-center px-3'>
              <div className='symbol symbol-50px me-5'>
                <img alt='Logo' src={(BACKEND_UPLOAD_URL + curPhotoURL) as string} />
              </div>

              <div className='d-flex flex-column'>
                <div className='fw-bolder d-flex align-items-center text-break text-center pe-2 fs-5'>
                  {curFirstName}&nbsp;{curSecondName}
                  <span className='badge badge-light-success fw-bolder fs-8 px-2 py-1 ms-2'>
                    {t('TOP_TOGGLEMENU_PRO')}
                  </span>
                </div>
                <a href='#' className='fw-bold text-muted text-hover-primary text-break fs-7'>
                  {curEmail}
                </a>
              </div>
            </div>
          </div>

          <div className='separator my-2'></div>

          <div className='menu-item px-5'>
            <Link
              to={permissionVal === 0 ? '/account/overview' : '/account/member'}
              className='menu-link px-5'
            >
              {t('TOP_TOGGLEMENU_PROFILE')}
            </Link>
          </div>

          <div className='separator my-2'></div>

          <div className='menu-item px-5'>
            <a
              onClick={logout}
              className='menu-link px-5'
              href={permissionVal === 0 ? '/auth/login' : '/staff/login'}
            >
              {t('TOP_TOGGLEMENU_LOGOUT')}
            </a>
          </div>
        </div>
      </ClickAwayListener>
    </>
  )
}

const mapStateToProps = (state: AppState) => ({
  inspectorMode: state.auth.inspectorMode,
  inspectorId: state.auth.inspector_id,
  curUserPhotoUrl: state.auth.photo_url,
  curUserFirstName: state.auth.first_name,
  curUserSecondName: state.auth.second_name,
  curUserEmail: state.auth.email,
})

export default connect(mapStateToProps)(HeaderUserMenu)
